import { useScreenSize } from '@hooks/useScreenSize';
import { Paper, Stack, Tab, Tabs } from '@mui/material';
import NotAvailablePage from '@pages/NotAvailablePage';
import NotFoundPage from '@pages/NotFoundPage';
import { OrderState } from '@protos/v2/order';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BidOfferOnly } from './BidOfferOnly';
import { AllOrdersGrid } from './components/AllOrdersGrid';
import { StyledAutocomplete } from './components/StyledAutocomplete';
import { ValidityInput } from './components/ValidityInput';
import { Mixed } from './Mixed/Mixed';
import { OrderEntry } from './OrderEntry';
import { OCLPricingSheet } from './pricing-sheet/OCLPricingSheet';
import { OCLTradingType } from './types/types';
import { AllOrdersOnly } from './AllOrdersOnly';
import { checkIsLadderUser } from '@utils/permissions';
type Tab = { label: string; value: string; Component: React.ComponentType<any> };
const OCLTradingTabs: Tab[] = [
  { label: 'PRICING SHEET', value: OCLTradingType.PRICING, Component: OCLPricingSheet },
  { label: 'MIXED', value: OCLTradingType.MIXED, Component: Mixed },
  { label: 'B/O ONLY', value: OCLTradingType.BID_OFFER_ONLY, Component: BidOfferOnly },
  { label: 'ORDER ENTRY', value: OCLTradingType.ORDER_ENTRY, Component: OrderEntry },
  { label: 'ALL ORDERS', value: OCLTradingType.ALL_ORDERS, Component: AllOrdersOnly },
];
const tradingAccountKey = 'flux-ocl-trading-account';

const TabLink = (props: any) => {
  return <Link {...props} to={props.href} />;
};

export const OCLTrading = () => {
  const user = useUserContext();

  const allowedOCLTrading = checkIsLadderUser(user) && user?.ladder_limits.length;

  const { isMobile, isMobileLandscape } = useScreenSize();
  const { activeTab: tabInURL } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [duration, setDuration] = useState(86400);

  const [selectedTab, setSelectedTab] = useState<Tab>(OCLTradingTabs[0]);
  const [selectedAccount, setSelectedAccount] = useState<string | ''>(localStorage.getItem(tradingAccountKey) || user?.abn_trad_accounts?.[0] || '');
  const [orderState, setOrderState] = useState<OrderState[]>([OrderState.NEW, OrderState.PARTIALLY_FILLED]);
  const [selectedProductSymbol, setSelectedProductSymbol] = useState<string | ''>();

  useEffect(() => {
    if (!tabInURL && selectedTab) {
      const params = searchParams.toString();
      navigate(`/ocl-trading/${selectedTab.value}${params ? `?${params}` : ''}`);
    } else if (!tabInURL && !selectedTab) {
      setSelectedTab(OCLTradingTabs[0]);
    } else if (tabInURL && !selectedTab) {
      const tab = OCLTradingTabs.find(tab => tab.value === tabInURL);
      if (tab) {
        setSelectedTab(tab);
      }
    } else if (tabInURL && selectedTab && tabInURL !== selectedTab.value) {
      const tab = OCLTradingTabs.find(tab => tab.value === tabInURL);
      if (tab) {
        setSelectedTab(tab);
      }
    }
  }, [tabInURL, selectedTab, searchParams]);

  const getLinkForTab = useCallback(
    (value: string) => {
      if (value === OCLTradingType.PRICING) {
        return `/ocl-trading/${value}`;
      } else {
        return `/ocl-trading/${value}?account_id=${selectedAccount}&order_state=${orderState.join(',')}${
          selectedProductSymbol ? `&product_symbol=${selectedProductSymbol}` : ''
        }`;
      }
    },
    [tabInURL, selectedAccount, orderState, selectedProductSymbol]
  );

  const updateSelectedAccount = (newAcc: string) => {
    localStorage.setItem(tradingAccountKey, newAcc);
    setSelectedAccount(newAcc);
  };

  if (!allowedOCLTrading) return <NotFoundPage />;
  if (isMobile || isMobileLandscape) return <NotAvailablePage />;

  return (
    <Stack display="flex" flexDirection="column" flex={1} gap={1} padding={2} height="100%">
      <Stack display="flex" flexDirection="row" gap={1} alignItems="center">
        <Paper style={{ flex: 1, maxWidth: 'calc(100vw - 400px)', minWidth: 'calc(100vw - 400px)' }}>
          <Tabs value={selectedTab.value} variant="scrollable">
            {OCLTradingTabs.map(tab => (
              <Tab
                href={getLinkForTab(tab.value)}
                LinkComponent={TabLink}
                key={tab.value}
                label={tab.label}
                value={tab.value}
                style={{ fontSize: 11 }}
              />
            ))}
          </Tabs>
        </Paper>

        {selectedTab.value !== 'pricing' ? (
          <StyledAutocomplete
            options={user?.abn_trad_accounts || []}
            getOptionLabel={(option: unknown) => (option as string).toUpperCase()}
            value={selectedAccount}
            onChange={(_, newValue) => updateSelectedAccount(newValue || '')}
            label="Account"
            isOptionEqualToValue={(option, value) => (option as string).toUpperCase() === (value as string).toUpperCase()}
          />
        ) : (
          <ValidityInput value={duration.toString()} onChange={value => setDuration(Number(value))} />
        )}
      </Stack>

      <Paper style={{ display: selectedTab.value === OCLTradingType.ALL_ORDERS ? 'none' : 'flex', flex: 1 }} key={selectedTab.value}>
        {React.createElement(selectedTab.Component, {
          selectedAccount,
          setSelectedAccount: updateSelectedAccount,
          duration: selectedTab.value === 'pricing' ? duration : undefined,
        })}
      </Paper>

      {selectedTab.value !== OCLTradingType.PRICING ? (
        <Paper style={{ height: '100%' }}>
          <AllOrdersGrid
            selectedAccount={selectedAccount}
            setOrderState={setOrderState}
            setSelectedProductSymbol={setSelectedProductSymbol}
            maxHeight={selectedTab.value === OCLTradingType.ALL_ORDERS ? window.innerHeight - 200 : undefined}
          />
        </Paper>
      ) : null}
    </Stack>
  );
};
